import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { formsAPI } from "../services/api";
import FormFilters from "./shared/FormFilters.jsx";
import FormsTable from "./shared/FormsTable.jsx";
import FormPagination from "./shared/FormPagination.jsx";
import FormModal from "./shared/FormModal.jsx";

const RecentSubmissions = ({ recent_submissions, isExpandable = false }) => {
   const [isExpanded, setIsExpanded] = useState(false);
   const [expandedForms, setExpandedForms] = useState([]);
   const [loading, setLoading] = useState(false);
   const [formTypeFilter, setFormTypeFilter] = useState("");
   const [conversationIdFilter, setConversationIdFilter] = useState("");
   const [errorStatusFilter, setErrorStatusFilter] = useState("");
   const [page, setPage] = useState(1);
   const [totalForms, setTotalForms] = useState(0);
   const [selectedForm, setSelectedForm] = useState(null);
   const [showModal, setShowModal] = useState(false);
   const [limit, setLimit] = useState(20);

   const loadExpandedForms = async () => {
      if (!isExpanded) return;

      setLoading(true);
      try {
         const response = await formsAPI.getForms({
            form_type: formTypeFilter,
            conversation_id: conversationIdFilter,
            error_status: errorStatusFilter,
            page: page,
            limit: limit,
         });

         if (response.success) {
            setExpandedForms(response.data || []);
            setTotalForms(
               response.total ||
                  response.totalCount ||
                  response.count ||
                  (response.data ? response.data.length : 0)
            );
         }
      } catch (err) {
         console.error("Error loading expanded forms:", err);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      loadExpandedForms();
   }, [isExpanded, formTypeFilter, conversationIdFilter, errorStatusFilter, page, limit]);

   const handleExpand = () => {
      setIsExpanded(true);
   };

   const handleCollapse = () => {
      setIsExpanded(false);
      // Reset filters when collapsing
      setFormTypeFilter("");
      setConversationIdFilter("");
      setErrorStatusFilter("");
      setPage(1);
      // Clear loading state immediately
      setLoading(false);
   };

   const handleFormTypeChange = (e) => {
      setFormTypeFilter(e.target.value);
      setPage(1);
   };

   const handleErrorStatusChange = (e) => {
      setErrorStatusFilter(e.target.value);
      setPage(1);
   };

   const handleConversationIdFilter = (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      setConversationIdFilter(formData.get("conversation_id") || "");
      setPage(1);
   };

   const clearFilters = () => {
      setFormTypeFilter("");
      setConversationIdFilter("");
      setErrorStatusFilter("");
      setPage(1);
   };

   const showFormModal = (form) => {
      setSelectedForm(form);
      setShowModal(true);
   };

   const closeModal = () => {
      setShowModal(false);
      setSelectedForm(null);
   };

   const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= Math.ceil(totalForms / limit)) {
         setPage(newPage);
      }
   };

   const handleLimitChange = (newLimit) => {
      setLimit(newLimit);
      setPage(1);
   };

   if (!isExpandable) {
      // Original simple view for non-expandable usage
      return (
         <div className="content-card full-width">
            <div className="card-header">
               <h3 className="card-title">
                  <i className="bi bi-file-text"></i>
                  Recent Submissions
               </h3>
               <div className="card-actions">
                  <Link to="/forms" className="btn-action primary">
                     View All
                     <i className="bi bi-arrow-right"></i>
                  </Link>
               </div>
            </div>
            <FormsTable
               forms={recent_submissions ? recent_submissions.slice(0, 5) : []}
               isCompact={true}
            />
         </div>
      );
   }

   // Expandable view with full functionality
   return (
      <>
         <div className="content-card full-width">
            <div className="card-header">
               <h3 className="card-title">
                  <i className="bi bi-file-text"></i>
                  Recent Submissions
               </h3>
               <div className="card-actions">
                  {!isExpanded ? (
                     <button onClick={handleExpand} className="btn-action primary">
                        <i className="bi bi-arrows-expand"></i>
                        Expand
                     </button>
                  ) : (
                     <button onClick={handleCollapse} className="btn-action secondary">
                        <i className="bi bi-arrows-collapse"></i>
                        Collapse
                     </button>
                  )}
               </div>
            </div>

            {!isExpanded ? (
               // Collapsed view - show recent submissions (same as non-expandable version)
               <FormsTable
                  forms={recent_submissions ? recent_submissions.slice(0, 5) : []}
                  isCompact={true}
               />
            ) : (
               // Expanded view - show full functionality
               <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  {/* Filter Bar */}
                  <FormFilters
                     formTypeFilter={formTypeFilter}
                     conversationIdFilter={conversationIdFilter}
                     errorStatusFilter={errorStatusFilter}
                     onFormTypeChange={handleFormTypeChange}
                     onConversationIdSubmit={handleConversationIdFilter}
                     onErrorStatusChange={handleErrorStatusChange}
                     onClearFilters={clearFilters}
                     isCompact={true}
                  />

                  {/* Pagination */}
                  {expandedForms.length > 0 && (
                     <div className="p-3 border-bottom">
                        <FormPagination
                           page={page}
                           limit={limit}
                           totalForms={totalForms}
                           onPageChange={handlePageChange}
                           onLimitChange={handleLimitChange}
                        />
                     </div>
                  )}

                  {/* Forms Table */}
                  {loading ? (
                     <div className="text-center p-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                           <span className="visually-hidden">Loading...</span>
                        </div>
                     </div>
                  ) : (
                     <div className="p-3">
                        <FormsTable forms={expandedForms} onRowClick={showFormModal} />
                        {expandedForms.length === 0 &&
                           (formTypeFilter || conversationIdFilter || errorStatusFilter) && (
                              <div className="text-center mt-3">
                                 <button onClick={clearFilters} className="btn btn-primary btn-sm">
                                    <i className="bi bi-arrow-clockwise"></i> Clear Filters
                                 </button>
                              </div>
                           )}
                     </div>
                  )}
               </div>
            )}
         </div>

         <FormModal selectedForm={selectedForm} showModal={showModal} onClose={closeModal} />
      </>
   );
};

export default RecentSubmissions;
